import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Instagram, MessageCircle, Check, AlertTriangle, Send, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

function ConnectionCard({ kind, data, onSave, onTest, onDisconnect }) {
  const isIG = kind === "instagram";
  const Icon = isIG ? Instagram : MessageCircle;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sandbox: data?.sandbox ?? true });

  useEffect(() => { setForm({ sandbox: data?.sandbox ?? true }); }, [data?.sandbox]);

  return (
    <div className="surface p-6" data-testid={`integration-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`h-11 w-11 rounded-xl grid place-items-center ${isIG ? "bg-gradient-to-br from-pink-500 to-orange-500" : "bg-gradient-to-br from-emerald-500 to-teal-500"} text-white shadow-sm`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-ink">{isIG ? "Instagram Graph" : "WhatsApp Cloud"}</h3>
            <p className="text-xs text-meta">{isIG ? "DMs · Comments → DM · Story replies" : "Cloud API · broadcasts · reminders"}</p>
          </div>
        </div>
        <span className={`pill ${
          data?.connected ? (data?.sandbox ? "pill-warning" : "pill-success") : "pill-neutral"
        }`}>
          {data?.connected ? (data?.sandbox ? "Sandbox" : "Live") : "Not connected"}
        </span>
      </div>

      {data?.connected && (
        <div className="mt-4 grid sm:grid-cols-2 gap-2 text-xs">
          {Object.entries(data?.public || {}).filter(([k]) => !["sandbox","connected"].includes(k)).map(([k,v]) => (
            <div key={k} className="flex justify-between rounded-lg bg-zinc-50 border border-line px-3 py-2">
              <span className="text-meta capitalize">{k.replace(/_/g," ")}</span>
              <span className="font-mono truncate ml-2 text-ink">{v || "—"}</span>
            </div>
          ))}
          {Object.entries(data?.secrets_preview || {}).map(([k,v]) => (
            <div key={k} className="flex justify-between rounded-lg bg-zinc-50 border border-line px-3 py-2">
              <span className="text-meta capitalize">{k.replace(/_/g," ")}</span>
              <span className="font-mono truncate ml-2 text-ink">{v}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={() => setOpen(!open)} className="rounded-full ashra-gradient-bg px-4 py-2 text-xs font-semibold text-white shadow-sm hover:shadow-md transition focus-ring" data-testid={`configure-${kind}`}>
          {data?.connected ? "Reconfigure" : "Connect"}
        </button>
        {data?.connected && (
          <>
            <button onClick={() => onTest(kind)} className="rounded-full border border-line bg-white text-ink-soft hover:bg-zinc-50 px-4 py-2 text-xs font-medium inline-flex items-center gap-1 transition focus-ring" data-testid={`test-send-${kind}`}>
              <Send className="h-3 w-3" /> Test send
            </button>
            <button onClick={() => onDisconnect(kind)} className="rounded-full border border-rose-200 bg-danger-soft text-danger hover:bg-rose-100 px-4 py-2 text-xs font-medium transition focus-ring">Disconnect</button>
          </>
        )}
      </div>

      {open && (
        <form
          onSubmit={(e) => { e.preventDefault(); onSave(kind, form); setOpen(false); }}
          className="mt-5 space-y-3 border-t border-line pt-5"
          data-testid={`form-${kind}`}
        >
          <label className="flex items-center gap-2 text-sm text-ink-soft">
            <input type="checkbox" checked={form.sandbox} onChange={(e) => setForm({ ...form, sandbox: e.target.checked })} className="accent-violet-600" />
            Sandbox mode (simulated send/receive)
          </label>
          {!form.sandbox && (
            <div className="rounded-xl border border-amber-200 bg-warning-soft p-3 text-xs text-warning inline-flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              You'll need a Meta Business app with Graph API permissions. Tokens are AES-encrypted at rest.
            </div>
          )}
          {isIG ? (
            <>
              <input placeholder="Instagram Business Account ID" onChange={(e) => setForm({ ...form, ig_business_account_id: e.target.value })} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <input placeholder="Facebook Page ID" onChange={(e) => setForm({ ...form, page_id: e.target.value })} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
              {!form.sandbox && (
                <input placeholder="Page Access Token" type="password" onChange={(e) => setForm({ ...form, page_access_token: e.target.value })} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
              )}
            </>
          ) : (
            <>
              <input placeholder="WhatsApp Phone Number ID" onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
              <input placeholder="WABA ID" onChange={(e) => setForm({ ...form, waba_id: e.target.value })} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
              {!form.sandbox && (
                <>
                  <input placeholder="Access Token" type="password" onChange={(e) => setForm({ ...form, wa_access_token: e.target.value })} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
                  <input placeholder="Webhook Verify Token (any string)" onChange={(e) => setForm({ ...form, verify_token: e.target.value })} className="w-full rounded-xl bg-white border border-line px-3 py-2 text-sm text-ink placeholder:text-meta-soft focus:outline-none focus:ring-2 focus:ring-violet-500" />
                </>
              )}
            </>
          )}
          <button className="w-full rounded-full ashra-gradient-bg py-2.5 text-sm font-semibold text-white shadow-sm hover:shadow-md transition" data-testid={`save-${kind}`}><Check className="inline h-4 w-4 mr-1" /> Save</button>
        </form>
      )}
    </div>
  );
}

export default function Integrations() {
  const [data, setData] = useState({ instagram: null, whatsapp: null });
  const [metaCfg, setMetaCfg] = useState(null);
  const [oauthBusy, setOauthBusy] = useState(null);

  const load = async () => {
    const [{ data }, cfg] = await Promise.all([
      api.get("/integrations"),
      api.get("/integrations/meta/config").catch(() => ({ data: null })),
    ]);
    setData(data);
    setMetaCfg(cfg.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (kind, form) => {
    try {
      await api.post(`/integrations/${kind}`, form);
      toast.success(form.sandbox ? "Connected (sandbox)" : "Connected");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };
  const onTest = async (kind) => {
    try {
      const { data } = await api.post(`/integrations/${kind}/test-send`, { text: "Hello from Ashra AI", recipient: kind === "instagram" ? "demo_user" : "+10000000000" });
      toast.success(data.sandbox ? "Sandbox send simulated" : `Sent · ${data.status}`);
    } catch { toast.error("Send failed"); }
  };
  const onDisc = async (kind) => {
    await api.post(`/integrations/${kind}/disconnect`);
    toast.success("Disconnected");
    load();
  };

  const connectViaOauth = async (kind) => {
    setOauthBusy(kind);
    try {
      const redirect = `${window.location.origin}/auth/callback`;
      const { data } = await api.post("/integrations/meta/oauth-url", { kind, redirect_url: redirect });
      // Sandbox mode → call callback directly so the integration is upserted.
      if (data.sandbox) {
        await api.get(`/integrations/meta/callback`, { params: { state: data.state, ashra_sandbox: 1 } });
        toast.success(`${kind} connected (sandbox OAuth)`);
        load();
      } else {
        const popup = window.open(data.url, "ashra-meta-oauth", "width=620,height=720");
        if (!popup) {
          toast.error("Popup blocked — allow popups to continue");
        }
        // Reload after a short delay so the callback can finish in the popup.
        setTimeout(load, 8000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OAuth failed");
    }
    setOauthBusy(null);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="integrations-page">
      <h1 className="font-display text-3xl font-bold text-ink">Integrations</h1>
      <p className="text-sm text-meta mt-1">Connect Meta channels via OAuth, or paste tokens manually. Sandbox mode lets you test the full flow.</p>

      {/* Meta OAuth banner */}
      <div className="mt-6 surface p-5" data-testid="meta-oauth-card">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 grid place-items-center text-white shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-lg font-bold text-ink">Meta OAuth</h3>
              <span className={`pill ${metaCfg?.configured ? "pill-success" : "pill-warning"}`}>
                {metaCfg?.configured ? "Live" : "Sandbox (env unset)"}
              </span>
            </div>
            <p className="text-xs text-meta mt-1">
              One-click connect for Instagram + WhatsApp. {metaCfg?.configured
                ? "Real Meta authorize flow active."
                : "Set META_APP_ID and META_APP_SECRET to enable live OAuth."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={() => connectViaOauth("instagram")}
                disabled={oauthBusy === "instagram"}
                data-testid="connect-meta-instagram"
                className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink-soft hover:border-ai hover:text-ai inline-flex items-center gap-1 transition"
              >
                {oauthBusy === "instagram" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Instagram className="h-3 w-3" />} Connect Instagram
              </button>
              <button
                onClick={() => connectViaOauth("whatsapp")}
                disabled={oauthBusy === "whatsapp"}
                data-testid="connect-meta-whatsapp"
                className="rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink-soft hover:border-ai hover:text-ai inline-flex items-center gap-1 transition"
              >
                {oauthBusy === "whatsapp" ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />} Connect WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-4">
        <ConnectionCard kind="instagram" data={data.instagram} onSave={save} onTest={onTest} onDisconnect={onDisc} />
        <ConnectionCard kind="whatsapp" data={data.whatsapp} onSave={save} onTest={onTest} onDisconnect={onDisc} />
      </div>

      <div className="mt-8 surface p-6">
        <h3 className="font-display text-lg font-bold text-ink">How sandbox works</h3>
        <p className="mt-2 text-sm text-meta leading-relaxed">In sandbox mode Ashra simulates Meta API responses so you can build flows, test the AI inbox, and verify automations end-to-end. When you're ready to go live, drop in your Meta credentials and toggle sandbox off — same code path, real APIs.</p>
      </div>
    </div>
  );
}
